import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoIcon from '@mui/icons-material/InfoRounded';
import React from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import {
  Box,
  Card,
  CardContent,
  Chip,
  Link,
  makeStyles,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';

import { CategoryScore, LabelsForStringTier } from '../../types';
import { getNotApplicableInfo } from '../../utils';

interface ScorecardInfoCardProps {
  categoryScore: CategoryScore;
  onCardClick?: () => void;
  isHighPriority?: boolean;
}

const getScoreColor = (theme: Theme, score: number | 'N/A'): string => {
  if (score === 'N/A') return theme.palette.grey[500];
  if (score === 100) return theme.palette.success.main;
  if (score >= 99) return theme.palette.info.main;
  if (score >= 90) return theme.palette.warning.main;
  return theme.palette.error.main;
};

const getTierColor = (theme: Theme, tier: string): string => {
  switch (tier) {
    case 'CRITICAL':
      return theme.palette.error.main;
    case 'CAUTION':
      return theme.palette.warning.main;
    case 'ON_TRACK':
      return theme.palette.info.main;
    case 'EXCELLENT':
      return theme.palette.success.main;
    default:
      return theme.palette.grey[500];
  }
};

const getHighPriorityLabel = (score: number): string => {
  if (score === 100) return 'Completed';
  if (score >= 99) return 'On Track';
  if (score >= 90) return 'Caution';
  return 'Critical';
};

const getTierChip = (
  theme: Theme,
  tier: string,
  score: number | 'N/A',
  isHighPriority?: boolean,
) => {
  if (score === 'N/A' || tier === 'N/A') return null;
  const color = isHighPriority
    ? getScoreColor(theme, score)
    : getTierColor(theme, tier);
  const label = isHighPriority
    ? getHighPriorityLabel(score)
    : LabelsForStringTier[tier] ?? tier;
  return (
    <Chip
      label={label}
      size="small"
      style={{
        backgroundColor: color,
        color: theme.palette.getContrastText(color),
        fontWeight: 600,
        fontSize: '0.65rem',
        height: 20,
      }}
    />
  );
};

const useStyles = makeStyles(theme => ({
  hoverableCard: {
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.6)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.6) !important',
    },
  },
  cardContent: {
    padding: '20px !important',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  leftSection: {
    flex: 1,
    minWidth: 0,
  },
  categoryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  categoryLabel: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  infoIcon: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    cursor: 'help',
  },
  description: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  progressSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  progressBarContainer: {
    position: 'relative',
    height: 8,
    borderRadius: 4,
    backgroundColor: '#e0e0e0',
    overflow: 'hidden',
  },
  progressBarFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    borderRadius: 4,
    transition: 'width 0.3s ease',
  },
  bottomRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
  },
  scoreText: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  periodCaption: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  naReason: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  naChip: {
    backgroundColor: '#E4E7EB',
    fontWeight: 600,
    fontSize: '0.65rem',
    height: 20,
  },
  naAction: {
    display: 'inline-block',
    alignSelf: 'flex-start',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginTop: 4,
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
  viewDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: theme.palette.primary.main,
    fontSize: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    whiteSpace: 'nowrap',
    '&:hover $chevronIcon': {
      transform: 'translateX(4px)',
    },
  },
  chevronIcon: {
    fontSize: '1rem',
    transition: 'transform 0.2s ease',
  },
}));

export const ScorecardInfoCard = ({
  categoryScore,
  onCardClick,
  isHighPriority,
}: ScorecardInfoCardProps) => {
  const { category, score, description, tier, failed, periodLabel, naReason } =
    categoryScore;
  const classes = useStyles();
  const theme = useTheme();
  const naInfo = getNotApplicableInfo(category, naReason);
  const [searchParams] = useSearchParams();

  const renderNaAction = () => {
    if (!naInfo?.action) return null;
    const stop = (e: React.MouseEvent) => e.stopPropagation();
    const linkProps = { className: classes.naAction, onClick: stop } as const;

    if (naInfo.actionLink?.type === 'route') {
      return (
        <Link component={RouterLink} to={naInfo.actionLink.to} {...linkProps}>
          {naInfo.action} →
        </Link>
      );
    }
    if (naInfo.actionLink?.type === 'category') {
      const next = new URLSearchParams(searchParams);
      next.set('category', naInfo.actionLink.category);
      return (
        <Link
          component={RouterLink}
          to={{ search: `?${next.toString()}` }}
          {...linkProps}
        >
          {naInfo.action} →
        </Link>
      );
    }
    return (
      <Typography className={classes.naAction}>{naInfo.action}</Typography>
    );
  };

  const numericScore = score === 'N/A' ? 0 : score;
  const scoreColor = isHighPriority
    ? getScoreColor(theme, score)
    : getTierColor(theme, tier);

  const scoreContent =
    score !== 'N/A' ? (
      <>
        <Box className={classes.progressBarContainer}>
          <Box
            className={classes.progressBarFill}
            style={{
              width: `${numericScore}%`,
              backgroundColor: scoreColor,
            }}
          />
        </Box>
        <Box className={classes.bottomRow}>
          <Box>
            <Typography className={classes.scoreText}>{score}%</Typography>
            {periodLabel && (
              <Typography className={classes.periodCaption}>
                as of {periodLabel}
              </Typography>
            )}
          </Box>
          <Box className={classes.viewDetails}>
            <span>View details</span>
            <ChevronRightIcon className={classes.chevronIcon} />
          </Box>
        </Box>
      </>
    ) : (
      <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
        <Box className={classes.bottomRow}>
          <Chip
            label={
              <span style={{ color: '#1F2937' }}>
                {naInfo?.label ?? 'Not Applicable'}
              </span>
            }
            size="small"
            className={classes.naChip}
          />
          <Tooltip
            interactive
            arrow
            title={
              <>
                <Typography variant="caption" component="div">
                  {naInfo?.reason ??
                    (description
                      ? `Not applicable for this group — ${description}`
                      : 'Not applicable for this group.')}
                </Typography>
                {naInfo?.action && (
                  <Typography
                    variant="caption"
                    component="div"
                    style={{ marginTop: 6, fontWeight: 600 }}
                  >
                    {naInfo.action}
                  </Typography>
                )}
              </>
            }
          >
            <InfoIcon className={classes.infoIcon} style={{ cursor: 'help' }} />
          </Tooltip>
        </Box>
        {naInfo?.reason && (
          <Typography className={classes.naReason}>{naInfo.reason}</Typography>
        )}
        {renderNaAction()}
      </Box>
    );

  return (
    <Card className={classes.hoverableCard} onClick={onCardClick}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.topRow}>
          <Box className={classes.leftSection}>
            <Box className={classes.categoryRow}>
              {description && (
                <Tooltip title={description} arrow>
                  <InfoIcon className={classes.infoIcon} />
                </Tooltip>
              )}
              <Typography className={classes.categoryLabel}>
                {category}
              </Typography>
            </Box>
            {description && (
              <Typography className={classes.description}>
                {description}
              </Typography>
            )}
          </Box>
          {failed ? (
            <Chip
              label="Failed"
              size="small"
              style={{
                backgroundColor: theme.palette.error.main,
                color: theme.palette.getContrastText(theme.palette.error.main),
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 20,
              }}
            />
          ) : (
            getTierChip(theme, tier, score, isHighPriority)
          )}
        </Box>

        <Box className={classes.progressSection}>
          {failed ? (
            <Box className={classes.bottomRow}>
              <Typography
                className={classes.scoreText}
                style={{
                  color: theme.palette.error.main,
                  fontSize: '0.95rem',
                }}
              >
                Couldn't load — try refreshing
              </Typography>
            </Box>
          ) : (
            scoreContent
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
